import { GridSortModel } from '@chronosphereio/chrono-ui';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChartInstance, LegendItem, SelectedLegendItemState, TableLegendPinnedColumns } from '@/components';
import { TableLegendColumns } from '@/components/Legend/TableLegend';
import { LegendSpecOptions } from '@/core';
import { useAppInteractionLevel } from '@/plugin-system';
import { TimeSeriesChartPanelContentProps } from '../TimeSeriesChartPanelContent';
import { TimeSeriesChartProps } from '../time-series-chart-model';
import { gridSortModelToSpecSort, specSortToGridSortModel } from '../utils/legend-sort';
import { TimeSeriesGroup } from './dataset';

const EMPTY_LEGEND_ITEMS: LegendItem[] = [];

interface UseTimeSeriesChartLegendProps
  extends Pick<
    TimeSeriesChartProps,
    'enableLabelsToColumns' | 'iconColumnHeaderName' | 'legendDensity' | 'enableTableLegendColumnMenu'
  > {
  legend?: LegendSpecOptions;
  legendItems?: LegendItem[];
  legendColumns: TableLegendColumns;
  customTableLegendColumnsStart?: TableLegendColumns;
  chartRef: React.RefObject<ChartInstance>;
  selectedLegendItems: SelectedLegendItemState;
  setSelectedLegendItems: React.Dispatch<React.SetStateAction<SelectedLegendItemState>>;
  hasFormattedNames?: boolean;
  disableListLegendItemTruncation?: boolean;
  hasSeriesIcons?: boolean;
  pinnedTableLegendColumns?: TableLegendPinnedColumns;
  timeSeriesGrouping?: TimeSeriesGroup;
  /** Used to persist and restore sort for the table legend */
  sortModel?: GridSortModel;
  /** Used to update the panel spec when the sort model changes */
  onSortModelChange?: (sortModel: GridSortModel) => void;
  hasLabels?: boolean;
}

export function useTimeSeriesChartLegendProps({
  legend,
  legendItems,
  legendColumns,
  customTableLegendColumnsStart,
  chartRef,
  selectedLegendItems,
  setSelectedLegendItems,
  enableLabelsToColumns,
  hasFormattedNames,
  disableListLegendItemTruncation,
  hasSeriesIcons,
  iconColumnHeaderName,
  legendDensity,
  enableTableLegendColumnMenu,
  pinnedTableLegendColumns,
  timeSeriesGrouping,
  onSortModelChange,
  sortModel,
  hasLabels,
}: UseTimeSeriesChartLegendProps) {
  const legendProps: TimeSeriesChartPanelContentProps['legendProps'] = useMemo(() => {
    return (
      legend && {
        options: legend,
        data: legendItems ?? EMPTY_LEGEND_ITEMS,
        selectedItems: selectedLegendItems,
        onSelectedItemsChange: setSelectedLegendItems,
        tableProps: {
          columns: legendColumns,
          customTableLegendColumnsStart,
          sortModel,
          onSortModelChange,
          // When showing labels as columns, only include the "Name" column when the user has
          // formatted the names (and thus it probably isn't just the raw metric with its labels, which
          // would be duplicative) or if there are no labels available (so that we fall back to
          // showing something).
          disableNameColumn: enableLabelsToColumns && !hasFormattedNames && hasLabels,
          // Only enable the icon column if we have series with icons.
          enableIconColumn: hasSeriesIcons,
          iconColumnHeaderName: iconColumnHeaderName,
          enableColumnMenu: enableTableLegendColumnMenu,
          pinnedColumns: pinnedTableLegendColumns,
        },
        onItemMouseOver: (e, { id }) => {
          const timeSeriesGroup = timeSeriesGrouping?.[id];
          if (timeSeriesGroup) {
            // If the item is a group, highlight all associated series.
            for (const seriesId of timeSeriesGroup) {
              chartRef.current?.highlightSeries({ name: `${seriesId}` });
            }
          } else {
            // Not a group. Highlight individual item.
            chartRef.current?.highlightSeries({ name: id });
          }
        },
        onItemMouseOut: () => {
          chartRef.current?.clearHighlightedSeries();
        },
        disableListLegendItemTruncation,
        density: legendDensity,
      }
    );
  }, [
    legend,
    legendItems,
    selectedLegendItems,
    setSelectedLegendItems,
    legendColumns,
    customTableLegendColumnsStart,
    enableLabelsToColumns,
    hasFormattedNames,
    hasLabels,
    hasSeriesIcons,
    iconColumnHeaderName,
    enableTableLegendColumnMenu,
    pinnedTableLegendColumns,
    disableListLegendItemTruncation,
    legendDensity,
    timeSeriesGrouping,
    chartRef,
    sortModel,
    onSortModelChange,
  ]);

  return legendProps;
}

/**
 * Manages table legend sort state and syncing with JSON spec.
 * In non-dashboard context, onPluginSpecChange is undefined (sort state is local only).
 */
export function useTimeSeriesChartLegendSort({
  legendSpec,
  onPluginSpecChange,
}: {
  legendSpec?: LegendSpecOptions;
  onPluginSpecChange?: (spec: { legend: LegendSpecOptions }, options?: { mergeUpdates?: boolean }) => void;
}) {
  // Whether panel is being used in view or edit mode
  const appInteractionLevel = useAppInteractionLevel();

  // Local sort model state for keeping view and edit modes in sync,
  // while only calling onPluginSpecChange when necessary.
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(() =>
    specSortToGridSortModel(legendSpec?.sort)
  );

  // Track whether saved legend spec is out of sync with local state.
  // This is needed because in view mode since we update local state but don't save to spec.
  // When switching to edit mode, we need to sync these "dirty" changes.
  const dirtySortModelRef = useRef<GridSortModel | undefined>();

  // Sync sort changes made in view mode when switching to edit mode.
  // Without this, changes made in view mode would be lost after user saves in edit mode.
  useEffect(() => {
    // Early return if not in dashboard edit mode with pending changes.
    if (!onPluginSpecChange || appInteractionLevel !== 'editable-edit') return;
    if (!dirtySortModelRef.current || !legendSpec) return;

    const convertedLegendSort = gridSortModelToSpecSort(dirtySortModelRef.current);

    // Clear dirty state before updating to prevent this effect from running again
    dirtySortModelRef.current = undefined;

    onPluginSpecChange(
      {
        legend: {
          ...legendSpec,
          sort: convertedLegendSort,
        },
      },
      { mergeUpdates: true }
    );
  }, [appInteractionLevel, onPluginSpecChange, legendSpec]);

  const onSortModelChange = (newSortModel: GridSortModel) => {
    // Always update local state immediately so UI reflects changes in both view and edit modes.
    // This allows the view mode sort state to be updated independently of the JSON spec.
    setSortModel(newSortModel);

    // Early return for non-dashboard contexts to avoid unnecessary computation.
    // When onPluginSpecChange is undefined, we only need to update local state.
    if (!onPluginSpecChange) {
      return;
    }

    // Track whether saved legend spec is out of sync with local state.
    const specModel = specSortToGridSortModel(legendSpec?.sort);
    const hasChanges = !isEqual(newSortModel, specModel);

    if (hasChanges) {
      // Mark legend spec as dirty when changes are made in view mode so we know to update if user
      // switches to edit mode. Important or the spec would be stale when user goes to save.
      dirtySortModelRef.current = newSortModel;
    } else {
      // Clear dirty state if sort matches the saved spec
      dirtySortModelRef.current = undefined;
    }

    // In dashboard edit mode, immediately update the panel spec.
    if (appInteractionLevel === 'editable-edit' && legendSpec) {
      const convertedLegendSort = gridSortModelToSpecSort(newSortModel);
      onPluginSpecChange(
        {
          legend: {
            ...legendSpec,
            sort: convertedLegendSort,
          },
        },
        { mergeUpdates: true }
      );
    }
  };

  return {
    sortModel,
    onSortModelChange,
  };
}
